'use client';

import { Step } from '@tiptap/pm/transform';
import {
  Editor,
  EditorContent,
  EditorEvents,
  Extensions,
  useEditor,
} from '@tiptap/react';
import classNames from 'classnames';

import AttributesSlashIconSvg from '@/assets/svgs/text-editor/attributes-slash.svg';
import StarterKit from '@tiptap/starter-kit';
import AttributesNode from './CustomNodes/AttributesNode';
import LinkNode from './CustomNodes/LinkNode';
// import MediaNode from './CustomNodes/MediaNode';
import { MediaItem } from '@/containers/MediaManager/types';
import { useAppDispatch } from '@/lib/redux/store';
import { InputContainer } from '@/shared/components/FormInputs';
import { replyStopMessage, UnicodeMap, UnicodeRegular } from '@/shared/utils/text-editor';
import { Fragment, ReactNode, useEffect, useMemo } from 'react';
import {
  setInitialMessageOptOutText,
  setIsShowingFloatingAttributes,
  setIsShowingFloatingEmoji,
  setIsShowingMediaManager,
} from '../Jobs/redux/jobEditors.slice';
import KeyboardShortcuts from './CustomExtensions/KeyboardShortcuts';
import MaxLength from './CustomExtensions/MaxLength';
import SlashCommand from './CustomExtensions/SlashCommand';
import SpanNode from './CustomNodes/SpanNode';
import useTextEditorShortcuts from './hooks/useTextEditorShortcuts';
import TextEditorToolbar from './TextEditorToolbar/TextEditorToolbar';
import { EditorStorageWithFormatting, ToolbarItem } from './types';
import { replaceRawAttributes } from './utils';

interface CustomStorageData {
  customData?: {
    optOutMessage: string;
    maxLength: number;
  };
}

interface TextEditorProps {
  className?: string;
  containerClassName?: string;
  placeholderText?: string;
  placeholderClassName?: string;
  maxLength?: number;
  onChange: (messageContent: string) => void;
  setEditor: (editor: Editor | null) => void;
  tools: ToolbarItem[];
  hideToolbar?: boolean;
  disallowKeyboardShortcut?: boolean;
  isInlineToolbar?: boolean;
  shouldAppendStopMessage?: boolean;
  optOutMessage?: string;
  setOptOutMessage?: (m: string) => void;
  preContent?: ReactNode;
  initialContent?: string;
  jobId?: string; // Add jobId prop for context-aware Redux actions
  onMediaSelected?: (media: MediaItem) => void; // Custom callback for media selection
}

const TextEditor = ({
  className,
  containerClassName,
  placeholderClassName,
  placeholderText,
  setEditor,
  onChange,
  tools,
  maxLength,
  hideToolbar,
  disallowKeyboardShortcut,
  isInlineToolbar,
  shouldAppendStopMessage,
  optOutMessage,
  setOptOutMessage,
  preContent,
  initialContent,
  jobId = '', // Default to empty string for backwards compatibility
  onMediaSelected,
}: TextEditorProps) => {
  const dispatch = useAppDispatch();
  const {
    handleOnSlashCommand,
    handleAttributesShortcut,
    handleEmojiShortcut,
    handleAddLink,
    handleAddMedia,
    handleBoldShortcut,
    handleItalicsShortcut,
  } = useTextEditorShortcuts(jobId);

  const extensions = useMemo(() => {
    const extensions: Extensions = [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        codeBlock: false,
      }),
      SpanNode,
    ];

    if (maxLength) {
      extensions.push(MaxLength);
    }

    if (tools.includes('attributes')) {
      extensions.push(AttributesNode);
      if (!disallowKeyboardShortcut) {
        extensions.push(SlashCommand.configure({ handleOnSlashCommand }));
      }
    }

    if (tools.includes('links')) {
      extensions.push(LinkNode);
    }

    // if (tools.includes('media')) {
    //   extensions.push(MediaNode);
    // }

    if (!disallowKeyboardShortcut) {
      extensions.push(
        KeyboardShortcuts.configure({
          handleAttributesShortcut: tools.includes('attributes')
            ? handleAttributesShortcut
            : null,
          handleEmojiShortcut: tools.includes('emoji') ? handleEmojiShortcut : null,
          handleBoldShortcut: tools.includes('b') ? handleBoldShortcut : null,
          handleItalicsShortcut: tools.includes('i') ? handleItalicsShortcut : null,
          handleAddLink: tools.includes('links') ? handleAddLink : null,
          handleAddMedia: tools.includes('media') ? handleAddMedia : null,
        })
      );
    }
    return extensions;
  }, [
    maxLength,
    tools,
    disallowKeyboardShortcut,
    handleOnSlashCommand,
    handleAttributesShortcut,
    handleEmojiShortcut,
    handleBoldShortcut,
    handleItalicsShortcut,
    handleAddLink,
    handleAddMedia,
  ]);

  const editor = useEditor({
    content: initialContent || '',
    extensions,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        style: 'outline: none; border: none; line-height: 1.8;',
      },
      handleKeyDown(_, event) {
        // Prevent default behavior for keyboard shortcuts
        // if (
        //   (event.metaKey || event.ctrlKey) &&
        //   ['b', 'i'].includes(event.key.toLowerCase())
        // ) {
        //   event.preventDefault(); // Disable default bold and italic shortcuts
        //   return true; // Indicate that the event was handled
        // }

        if (isInlineToolbar) {
          if (event.key === 'Enter') {
            // do something
            return true;
          }
        }

        // Allow other key events to proceed normally
        return false;
      },
    },

    onCreate: ({ editor }) => {
      // Add custom data to storage
      const customStorage = editor.storage as CustomStorageData;
      if (!customStorage.customData) {
        customStorage.customData = {
          optOutMessage: '',
          maxLength: 0,
        };
      }
    },

    onFocus() {
      dispatch(setIsShowingFloatingAttributes({ jobId, isShowing: false }));
      dispatch(setIsShowingFloatingEmoji({ jobId, isShowing: false }));
      dispatch(setIsShowingMediaManager({ jobId, isShowing: false }));
    },

    onUpdate: ({ editor }) => {
      if (!editor) {
        return;
      }

      const html = editor.getHTML();

      // Only transform if there are raw {attributes} present
      const hasRawAttributes = /{[a-zA-Z_]+}/.test(html);

      if (hasRawAttributes) {
        const formattedHtml = replaceRawAttributes(html);

        // Only setContent if transformation actually occurred
        if (html !== formattedHtml) {
          console.log('🔧 TextEditor Transformation:', {
            jobId,
            originalHtml: html,
            transformedHtml: formattedHtml,
          });

          // Save cursor position
          const { from, to } = editor.state.selection;

          // Update content and restore cursor
          editor
            .chain()
            .setContent(formattedHtml)
            .setTextSelection({
              from: Math.min(from, editor.state.doc.content.size),
              to: Math.min(to, editor.state.doc.content.size),
            })
            .run();
        }
      }

      // Always call onChange with current content
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    setEditor(editor);
  }, [setEditor, editor]);

  // Handle text input with formatting state
  useEffect(() => {
    if (!editor) return;

    let isTransforming = false; // Prevent concurrent transformations

    const handleTransaction = ({
      editor: ed,
      transaction,
    }: EditorEvents['transaction']) => {
      // Only process if there are actual changes to the document
      if (!transaction.docChanged) return;

      // Skip if we're already transforming to prevent race conditions
      if (isTransforming) return;

      const storage = ed.storage as EditorStorageWithFormatting<typeof ed.storage>;
      const formattingState = storage?.formattingState;

      if (!formattingState) return;

      const { isBold, isItalics, fontName } = formattingState;

      // Determine the font variant to use
      let selectedFontName = fontName || 'regular';
      if ((!fontName || fontName === 'regular') && (isBold || isItalics)) {
        selectedFontName = 'sans-serif';
      }

      const finalFontName = `${selectedFontName}${isBold ? '-bold' : ''}${isItalics ? '-italic' : ''}`;

      // Don't transform if we're using regular font with no bold/italic
      if (finalFontName === 'regular') return;

      const fmtmap = UnicodeMap.get(finalFontName);

      if (!fmtmap) return;

      // Helper function to check if a character is already transformed
      const isAlreadyTransformed = (char: string): boolean => {
        // Check if this character exists in any of the Unicode map values
        // but NOT in the regular characters
        if (UnicodeRegular.includes(char)) {
          return false; // It's a regular character, needs transformation
        }

        // Check if it's already a transformed character
        for (const [, transformedChars] of UnicodeMap) {
          if (transformedChars.includes(char)) {
            return true; // Already transformed
          }
        }

        return false; // Not a transformable character
      };

      // Check if new text was inserted
      transaction.steps.forEach((step: Step) => {
        // Type assertion for ReplaceStep which has slice and from properties
        const replaceStep = step as Step & {
          slice?: {
            content: {
              size: number;
              textBetween: (start: number, end: number) => string;
            };
          };
          from?: number;
        };
        if (replaceStep.slice && replaceStep.slice.content.size > 0) {
          const content = replaceStep.slice.content.textBetween(
            0,
            replaceStep.slice.content.size
          );

          if (!content) return;

          // Check if this looks like text that might need transformation
          // Allow both regular characters and already-transformed characters
          const hasRegularChars = /[a-zA-Z0-9\s.,!?]/.test(content);

          if (!hasRegularChars) return; // No transformable content

          // Transform the text character by character
          // Skip characters that are already transformed
          let transformedText = '';
          let hasTransformation = false;

          for (let i = 0; i < content.length; i++) {
            const c = content[i];

            // If character is already transformed, keep it as-is
            if (isAlreadyTransformed(c)) {
              transformedText += c;
              continue;
            }

            // Try to transform regular characters
            const index = UnicodeRegular.indexOf(c);
            if (index !== -1) {
              transformedText += fmtmap[index];
              hasTransformation = true;
            } else {
              transformedText += c;
            }
          }

          // If transformation occurred, replace the text
          if (hasTransformation && transformedText !== content) {
            const from = replaceStep.from ?? 0;
            const to = from + content.length;

            // Set flag to prevent concurrent transformations
            isTransforming = true;

            // Use setTimeout to avoid conflicts with the current transaction
            setTimeout(() => {
              if (!ed.isDestroyed) {
                ed.chain()
                  .deleteRange({ from, to })
                  .insertContentAt(from, transformedText)
                  .run();

                // Reset flag after a short delay
                setTimeout(() => {
                  isTransforming = false;
                }, 10);
              }
            }, 0);
          }
        }
      });
    };

    editor.on('transaction', handleTransaction);

    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);

  // Handle initialContent prop changes
  useEffect(() => {
    if (!editor || !initialContent || initialContent.trim() === '') return;

    const currentContent = editor.getHTML();
    const isCurrentEmpty = currentContent === '<p></p>' || currentContent === '';

    const needsTransformation =
      initialContent.includes('{') && initialContent.includes('}');

    // Only update if:
    // 1. Editor is empty and content is different, OR
    // 2. initialContent contains {attributes} that need transformation AND the
    //    editor's current rendered HTML doesn't already match the transformed form
    //    (this prevents re-running the rebuild on every keystroke once the
    //    transformation has already converted the {tokens} to atom nodes)
    const processedContent = needsTransformation
      ? replaceRawAttributes(initialContent)
      : initialContent;

    // Rebuild when the editor still holds raw {token} text that needs to
    // become atom nodes (initial hydration with saved content). Once the
    // atoms are in place, the parent's derived state echoing back as
    // `initialContent` must NOT trigger a rebuild — the editor owns its
    // content during user editing, and a rebuild here jumps the cursor.
    const hasUntransformedTokens =
      needsTransformation && replaceRawAttributes(currentContent) !== currentContent;

    const shouldRebuild =
      (isCurrentEmpty && currentContent !== initialContent) || hasUntransformedTokens;

    if (!shouldRebuild) return;

    // Preserve the user's cursor across the rebuild.
    const { from, to } = editor.state.selection;

    // Use setTimeout to avoid React warnings about updates during render.
    const timeoutId = setTimeout(() => {
      if (!editor || editor.isDestroyed) return;
      editor
        .chain()
        .setContent(processedContent)
        .setTextSelection({
          from: Math.min(from, editor.state.doc.content.size),
          to: Math.min(to, editor.state.doc.content.size),
        })
        .run();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [editor, initialContent, jobId]);

  // Dynamic Opt Out Message
  useEffect(() => {
    if (shouldAppendStopMessage && editor) {
      const transformedOptOutMessage = optOutMessage ?? '';
      // const transformedOptOutMessage = optOutMessage ? `\n${optOutMessage}` : '';
      // console.log('TextEditor', { optOutMessage });
      dispatch(
        setInitialMessageOptOutText({ jobId, optOutText: transformedOptOutMessage })
      );

      const customStorage = editor.storage as CustomStorageData;
      if (customStorage.customData) {
        customStorage.customData.optOutMessage = transformedOptOutMessage;
      } else {
        customStorage.customData = {
          optOutMessage: transformedOptOutMessage,
          maxLength: maxLength || 0,
        };
      }
      // console.log('TextEditor', {
      //   optOutMessage,
      //   'editor.storage.customData.optOutMessage':
      //     editor.storage.customData.optOutMessage,
      // });
    }
  }, [dispatch, editor, jobId, optOutMessage, shouldAppendStopMessage, maxLength]);

  // Dynamic Max Length
  useEffect(() => {
    if (editor) {
      const customStorage = editor.storage as CustomStorageData;
      if (customStorage.customData) {
        customStorage.customData.maxLength = maxLength || 0;
      } else {
        customStorage.customData = {
          maxLength: maxLength || 0,
          optOutMessage: '',
        };
      }
    }
  }, [editor, maxLength]);

  return (
    <div className={className}>
      {!isInlineToolbar && !hideToolbar && (
        <TextEditorToolbar
          className={classNames('', {
            'fixed -top-1 -left-1 h-[1px] w-[1px] overflow-hidden p-0': hideToolbar,
            'pb-4': !hideToolbar,
          })}
          editor={editor}
          tools={tools}
          jobId={jobId}
          onMediaSelected={onMediaSelected}
        />
      )}
      <div className="px-2">{preContent}</div>

      <div className="relative">
        {/* Text Editor */}
        {editor && (
          <div
            className={classNames('relative cursor-text', containerClassName)}
            onMouseDown={(e) => {
              // Clicking the empty area of the editor (below the text) should
              // focus the editor instead of doing nothing. Only handle clicks
              // on the container itself, not on the editable content.
              if (e.target === e.currentTarget) {
                e.preventDefault();
                editor.commands.focus('end');
              }
            }}>
            {/* Placeholder content - lives inside the editor's padded box and
                shares its line-height so it aligns exactly with typed text */}
            {editor.isEmpty && (
              <div
                className={classNames(
                  'text-gray-default pointer-events-none absolute top-0 right-0 left-0 z-10 flex items-center gap-1 leading-[1.8]',
                  placeholderClassName
                )}>
                {placeholderText}
                {typeof placeholderText === 'undefined' && (
                  <Fragment>
                    <span>Start typing{disallowKeyboardShortcut ? '...' : '. Type'}</span>
                    {!disallowKeyboardShortcut && (
                      <Fragment>
                        <AttributesSlashIconSvg className="text-gray-400 dark:text-gray-500" />
                        <span>for attributes...</span>
                      </Fragment>
                    )}
                  </Fragment>
                )}
              </div>
            )}

            <EditorContent
              editor={editor}
              className={classNames('w-full bg-transparent pb-4', className)}
            />
            {shouldAppendStopMessage && (
              <div className="flex">
                <InputContainer label="" className="w-auto">
                  <input
                    type="text"
                    name="opt_out_message"
                    onChange={(e) => {
                      setOptOutMessage?.(e.currentTarget.value);
                    }}
                    value={optOutMessage}
                    placeholder={replyStopMessage}
                    className="input input-xs input-ghost !h-7 !min-h-7 w-full !border-none px-1 py-1 hover:bg-gray-100 focus:!bg-transparent dark:hover:bg-gray-800"
                  />
                </InputContainer>
              </div>
            )}

            {isInlineToolbar && !hideToolbar && (
              <TextEditorToolbar
                isInlineToolbar
                editor={editor}
                tools={tools}
                jobId={jobId}
                onMediaSelected={onMediaSelected}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextEditor;
